import {
  registerDescriber,
  tooltipText,
  type AccessibleNode,
} from "../core/accessibility.js";
import { registerAccessibleItemSources, resetAccessibleItemSources } from "./itemSources.js";
import { Chart } from "../controls/Chart.js";
import { ChartPanes } from "../controls/ChartPanes.js";
import { ComboBox } from "../controls/ComboBox.js";
import { DataGrid } from "../controls/DataGrid.js";
import { ListBox } from "../controls/ListBox.js";
import { ListView } from "../controls/ListView.js";
import { MenuBar } from "../controls/MenuBar.js";
import { MenuPopup } from "../controls/MenuPopup.js";
import { NavigationIndex } from "../controls/NavigationIndex.js";
import { Toolbar } from "../controls/Toolbar.js";
import { TreeView } from "../controls/TreeView.js";
import { LinkLabel } from "../controls/LinkLabel.js";
import { ScrollBar } from "../controls/ScrollBar.js";
import { Separator } from "../controls/Separator.js";
import { StatusBar } from "../controls/StatusBar.js";
import { TabControl } from "../controls/TabControl.js";
import { Window } from "../controls/Window.js";

/**
 * What the controls that cannot be guessed from their shape are called.
 *
 * `describeByShape` already carries most of the library - a caption plus a
 * tab stop is a button, whatever class it belongs to. What is left is the
 * handful whose role is not deducible from the properties they happen to
 * have: a window is a dialog or a region depending on how it was opened, a
 * link is a label that is not a label, a scroll bar is chrome rather than
 * content.
 *
 * This module is the one place in `src/a11y` that imports controls, which is
 * why it is here and not in `core/accessibility.ts`: core must not depend on
 * `src/controls` or the two bundles fuse.
 */
let registered = false;

/** Idempotent, and called only when a desktop turns accessibility on. */
export function registerAccessibleDescribers(): void {
  if (registered) return;
  registered = true;
  registerAccessibleItemSources();

  /**
   * A window is a dialog when it is modal and a region when it is not.
   *
   * The difference is not cosmetic: `dialog` is what tells a reader that the
   * rest of the page is unavailable, and applying it to every window would
   * say that about four things at once.
   */
  registerDescriber(Window, (control) => {
    const win = control as Window;
    return {
      role: win.modal ? "dialog" : "region",
      name: win.title || tooltipText(win.tooltip),
    };
  });

  registerDescriber(LinkLabel, (control) => {
    const link = control as LinkLabel;
    return { role: "link", name: link.text.trim() || tooltipText(link.tooltip) };
  });

  /**
   * The strip along the bottom announces itself when it changes.
   *
   * `polite` rather than `assertive`: a status line is worth hearing at the
   * next pause, and never worth interrupting someone mid-word for.
   */
  registerDescriber(StatusBar, () => ({ role: "status", name: "", live: "polite" }));

  registerDescriber(Separator, () => ({ role: "separator", name: "" }));

  /**
   * A scroll bar is chrome, and contributes nothing.
   *
   * It would otherwise describe itself as a slider - it has a value between a
   * minimum and a maximum, which is exactly the shape the fallback looks for -
   * and every scrollable control in the application would offer a reader two
   * draggable sliders it has no reason to want. Scrolling is something the
   * mirror does on the user's behalf by keeping the focused row in the window,
   * not something to hand over as a control.
   */
  registerDescriber(ScrollBar, () => null);

  registerDescriber(ComboBox, (control) => {
    const combo = control as ComboBox<unknown>;
    return {
      role: "combobox",
      name: tooltipText(combo.tooltip),
      valueText: combo.text,
      expanded: combo.droppedDown,
      ...(combo.editable ? {} : { readOnly: true }),
    };
  });

  /** The tabs themselves are rows this control draws, and arrive with the item sources. */
  registerDescriber(TabControl, (control) => ({
    role: "tablist",
    name: tooltipText(control.tooltip),
  }));

  // The data controls. None of these has a caption, a value or a checked
  // state, so the shape reader says nothing about them - and saying nothing
  // about a list also loses every row inside it, because rows are only
  // collected for a control that kept an element of its own.

  registerDescriber(ListBox, (control) => {
    const list = control as ListBox<unknown>;
    return {
      role: "listbox",
      name: tooltipText(list.tooltip),
      ...(list.selectionMode === "multiple" ? { multiSelectable: true } : {}),
    };
  });

  registerDescriber(ListView, (control) => {
    const view = control as ListView<unknown>;
    return {
      role: "listbox",
      name: tooltipText(view.tooltip),
      ...(view.selectionMode === "multiple" ? { multiSelectable: true } : {}),
    };
  });

  /**
   * A grid, announced as a list.
   *
   * `role="grid"` is the honest name and the wrong choice here: a grid's rows
   * have to contain `gridcell`s, the item model is flat, and an invalid grid
   * reads worse than a valid list. The row count still comes through, on each
   * row, as `aria-setsize`.
   */
  registerDescriber(DataGrid, (control) => ({
    role: "listbox",
    name: tooltipText(control.tooltip),
  }));

  registerDescriber(TreeView, (control) => {
    const tree = control as TreeView<never>;
    return {
      role: "tree",
      name: tooltipText(tree.tooltip),
      ...(tree.selectionMode === "multiple" ? { multiSelectable: true } : {}),
    };
  });

  registerDescriber(MenuBar, (control) => ({
    role: "menubar",
    name: tooltipText(control.tooltip),
    orientation: "horizontal",
  }));

  registerDescriber(MenuPopup, (control) => ({
    role: "menu",
    name: tooltipText(control.tooltip),
    orientation: "vertical",
  }));

  registerDescriber(Toolbar, (control) => ({
    role: "toolbar",
    name: tooltipText(control.tooltip),
  }));

  /**
   * A chart is a picture with a caption: its title for the name, and the series for the
   * description, since nothing else of it reaches a reader. The category picked out with the arrow
   * keys is added to the description, so scrubbing through reads each one's values.
   */
  registerDescriber(Chart, (control) => {
    const chart = control as Chart;
    const shape = chart.radial
      ? `${chart.chartType} chart, ${chart.categoryCount} slices`
      : chart.chartType === "treemap"
        ? `treemap, ${chart.categoryCount} tiles`
      : [
          chart.stacked ? "stacked" : "",
          chart.orientation === "horizontal" ? "horizontal" : "",
          chart.scatter ? "scatter chart" : "chart",
          chart.xScale === "category" ? `${chart.categoryCount} categories` : `${chart.xScale} x axis`,
          chart.valueScale === "log" ? "log value axis" : "",
          chart.hasSecondaryAxis ? "two value axes" : "",
          chart.zoomed ? "zoomed in" : "",
        ]
          .filter(Boolean)
          .join(" ")
          .replace(" chart ", " chart, ");
    const series = chart.series.map((s) => (chart.radial ? s.name : `${s.name} (${chart.kindOf(s)})`)).join(", ");
    const summary = `${shape}. ${series}.`;
    // What the tooltip shows, with its dash for a missing value read out as words.
    const hover = chart.hoverSummary().replaceAll("—", "no value");
    const picked = hover ? ` ${hover}` : "";
    return {
      role: "img",
      name: chart.title || tooltipText(chart.tooltip) || "Chart",
      description: summary + picked,
    };
  });

  /** Stacked chart panes read as one group, named by the top pane's title. */
  registerDescriber(ChartPanes, (control) => {
    const panes = control as ChartPanes;
    return { role: "group", name: tooltipText(panes.tooltip) || panes.panes[0]?.title || "Chart panes" };
  });

  /**
   * The navigation index reads as the list it is.
   *
   * It takes typed characters, to filter, which is enough for the shape
   * reader to call it a text box - so a reader would announce the outline of
   * the whole desktop as an empty edit field. Registering it also means the
   * index is usable through the mirror, which is the point: one definition of
   * what everything is called, and both ways in agree.
   */
  registerDescriber(NavigationIndex, () => ({
    role: "listbox",
    name: "Navigation index",
  }));
}

/** Exposed for a test that wants the registry rebuilt. */
export function resetAccessibleDescribers(): void {
  registered = false;
  resetAccessibleItemSources();
}

export type { AccessibleNode };
